import { Link } from 'react-router-dom';
import { seoArticles, comingSoonArticles } from '@/mocks/articles';
import type { FC } from 'react';

interface ArticleCard {
  slug?: string;
  title: string;
  excerpt: string;
  category: string;
  readTime?: number;
  date?: string;
  image: string;
  comingSoon?: boolean;
}

const articleCards: ArticleCard[] = [
  ...seoArticles.map((a) => ({
    slug: a.slug,
    title: a.title,
    excerpt: a.excerpt,
    category: a.category,
    readTime: a.readTime,
    date: a.date,
    image: a.image,
    comingSoon: false,
  })),
  ...comingSoonArticles,
];

const SeoBlogSection: FC = () => {
  return (
    <section id="blog" className="py-16 md:py-24 bg-background-100">
      <div className="section-container">
        <div className="text-center mb-12 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4">
            <i className="ri-article-line text-sm"></i>
            <span>Artykuły SEO</span>
          </div>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
            Kakobuy Spreadsheet – Baza wiedzy
          </h2>
          <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
            Poznaj nasze artykuły i poradniki o Kakobuy Spreadsheet. Praktyczna wiedza, porady zakupowe i bezpieczeństwo – wszystko w jednym miejscu.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {articleCards.map((article, i) => (
            <div
              key={article.slug || `soon-${i}`}
              className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 animate-fade-in-up"
              style={{ animationDelay: `${i * 0.08}s` }}
            >
              <div className="relative h-44 md:h-48 overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                {article.comingSoon && (
                  <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-background-900/75 backdrop-blur-sm text-background-50 text-[10px] font-medium">
                    Wkrótce
                  </div>
                )}
                <div className="absolute top-3 left-3">
                  <span className="px-2.5 py-1 rounded-full bg-background-50/90 backdrop-blur-sm text-foreground-700 text-[10px] font-medium">
                    {article.category}
                  </span>
                </div>
              </div>

              <div className="p-5">
                <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 leading-snug group-hover:text-primary-600 transition-colors">
                  {article.title}
                </h3>
                <p className="text-xs md:text-sm text-foreground-500 leading-relaxed mb-4 line-clamp-2">
                  {article.excerpt}
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-[11px] text-foreground-400">
                    {article.date && (
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {article.date}
                      </span>
                    )}
                    {article.readTime && (
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {article.readTime} min
                      </span>
                    )}
                  </div>

                  {article.comingSoon ? (
                    <span className="text-[11px] text-foreground-400 italic">W przygotowaniu</span>
                  ) : (
                    <Link
                      to={`/artykul/${article.slug}`}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                    >
                      Czytaj
                      <i className="ri-arrow-right-line text-xs"></i>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SeoBlogSection;